// FILECONTENT_START src/components/DownloadButton/DownloadButton.tsx
import DownloadIcon from "@mui/icons-material/Download";
import { Button, ButtonProps, CircularProgress, styled } from "@mui/material";
import api from "app/api";
import React, { useEffect, useState } from "react";

const StyledButton = styled(Button)(({ theme }) => ({
  //backgroundColor: theme.palette.common.white,
  paddingLeft: "16px",
  paddingRight: "16px",
  "&:hover": {
    //backgroundColor: theme.palette.primary.main,
  },
  position: "relative",
  display: "flex",
  alignItems: "center",
}));

const LoadingIndicator = styled(CircularProgress)(({ theme }) => ({
  color: theme.palette.primary.main,
}));

interface DownloadButtonProps extends ButtonProps {
  url?: string;
  filename?: string;
  automaticDownloadTime?: number;
  params?: any;
}

const mimeToExtension: Record<string, string> = {
  "application/pdf": "pdf",
  "application/msword": "doc",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "docx",
  "application/vnd.ms-excel": "xls",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
  "application/vnd.ms-powerpoint": "ppt",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation":
    "pptx",
  "text/plain": "txt",
  "text/csv": "csv",
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/gif": "gif",
  "image/bmp": "bmp",
  "image/webp": "webp",
  "audio/mpeg": "mp3",
  "audio/wav": "wav",
  "video/mp4": "mp4",
  "video/x-msvideo": "avi",
  "video/x-matroska": "mkv",
  "application/zip": "zip",
  "application/x-rar-compressed": "rar",
};

const getExtensionFromMimeType = (mimeType: string): string => {
  return mimeToExtension[mimeType] || "";
};

const DownloadButton: React.FC<DownloadButtonProps> = ({
  url,
  filename,
  automaticDownloadTime,
  params,
  ...rest
}) => {
  const [loading, setLoading] = useState(false);

  const handleDownload = async () => {
    if (!url) {
      return;
    }
    setLoading(true);
    try {
      const response = await api.get(url, {
        params,
        responseType: "blob",
      });

      const contentType = response.headers
        ? response.headers["content-type"]
        : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const extension = getExtensionFromMimeType(contentType);
      const finalFilename = filename || `downloaded_file.${extension}`;

      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", finalFilename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Download error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (automaticDownloadTime && url) {
      const timer = setTimeout(handleDownload, automaticDownloadTime * 1000);
      return () => clearTimeout(timer);
    }
  }, [automaticDownloadTime, url]);

  return (
    <StyledButton
      startIcon={<DownloadIcon />}
      onClick={handleDownload}
      disabled={loading}
      endIcon={loading ? <LoadingIndicator size={18} /> : undefined}
      {...rest}
    />
  );
};

export default DownloadButton;
// FILECONTENT_END src/components/DownloadButton/DownloadButton.tsx
